/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useLayoutEffect, useState } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import CartLineRow from "../components/CartLineRow";
import EmptyView from "../components/EmptyView";
import Icon from "../components/ui/Icon";
import Text from "../components/ui/Text";
import Title from "../components/ui/Title";
import Button from "../components/ui/Button";
import ButtonGroup from "../components/ui/ButtonGroup";
import Form from "../components/ui/form/Form";
import FormFieldsWrapper from "../components/ui/form/FormFieldsWrapper";
import FormError from "../components/ui/form/FormError";
import LoadingIndicator from "../components/LoadingIndicator";
import { useHeldHeight } from "../hooks/useHeldHeight";
import PriceBreakdown from "../components/booking/PriceBreakdown";
import ClientAuth from "../components/booking/ClientAuth";
import ClientTermsCheckboxes from "../components/booking/form/ClientTermsCheckboxes";
import GuestClientFields from "../components/booking/form/GuestClientFields";
import { useCart } from "../hooks/useCart";
import { useOrderLines } from "../hooks/checkout/useOrderLines";
import { useDirectOrder } from "../hooks/useDirectOrder";
import { useCartSummary } from "../hooks/useCartSummary";
import { useCheckoutCart } from "../hooks/checkout/useCheckoutCart";
import { useAuth } from "../hooks/useAuth";
import { useAuthSuccessHandler } from "../hooks/useAuthSuccessHandler";
import { useRequiredFieldsCompletion } from "../hooks/useRequiredFieldsCompletion";
import { useMembershipCheckout } from "../hooks/checkout/useMembershipCheckout";
import { useTranslate } from "../hooks/useTranslate";
import usePluginUtils from "../hooks/usePluginUtils";
import { useClientTerms } from "../lib/react-query/useClientTerms";
import { CART_LINE_TYPES, ROUTES } from "../constants/constants";

const Summary = styled.div`
  background: rgb(${({ theme }) => theme.colors.background.rgb});
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 1rem;
  box-shadow: 2px 2px 15px 0 rgba(${({ theme }) => theme.colors.text.rgb}, 0.10);
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
`;

const SummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
`;

const StyledPriceBreakdown = styled(PriceBreakdown)`
  margin-top: 1rem;
`;

const SummaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledEmptyView = styled(EmptyView)`
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const OrderSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDirectEntry = !!location.state?.directOrder;
  const { entryRoute, setLineQty, deleteLine } = useCart();
  const { lines, isDirectOrder } = useOrderLines();
  const { hasDirectOrder, cancelDirectOrder } = useDirectOrder();
  const cartSummary = useCartSummary(lines);
  const { client } = useAuth();
  const { _t } = useTranslate();
  const { hasActiveRecurringProfileFresh } = useMembershipCheckout();

  // Only the membership Buy flow sets location.state.directOrder; any other
  // path here means the direct order is stale and would shadow the cart.
  useLayoutEffect(() => {
    if (hasDirectOrder && !isDirectEntry) {
      cancelDirectOrder();
    }
  }, [hasDirectOrder, isDirectEntry, cancelDirectOrder]);

  const form = useForm({
    defaultValues: {
      name: client?.name ?? "",
      email: client?.email ?? "",
      phone: client?.phone ?? "",
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError,
  } = form;
  const { isPluginEnabled } = usePluginUtils();
  const { cartCheckout, isPending, error: checkoutError } = useCheckoutCart();
  const { data: terms, isLoading: termsLoading } = useClientTerms();
  const { isComplete } = useRequiredFieldsCompletion({ form });

  const clientLoginEnabled = isPluginEnabled("client_login");
  const showAuth = clientLoginEnabled && !client;
  const showForm = !showAuth;
  const showGuestFields = !clientLoginEnabled;

  // A logged-out client can slip past the Buy-button guard — re-check on submit.
  const recurringMembershipId =
    isPluginEnabled("membership") &&
    isDirectOrder &&
    lines[0]?.type === CART_LINE_TYPES.MEMBERSHIP &&
    lines[0]?.snapshot?.is_recurring
      ? lines[0].params?.membership_id ?? null
      : null;

  const handleContinueShopping = () => {
    if (isDirectOrder) {
      cancelDirectOrder();
      navigate(ROUTES.MEMBERSHIPS);
      return;
    }
    navigate(entryRoute || ROUTES.HOME);
  };

  const handleRemoveLine = (line) => {
    if (isDirectOrder) {
      cancelDirectOrder();
      return;
    }
    deleteLine(line);
  };

  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const heldHeight = useHeldHeight(isAutoSubmitting);

  const handleProceed = async (data) => {
    // Fresh read — the client may have just signed in on this page.
    if (recurringMembershipId != null && client) {
      if (await hasActiveRecurringProfileFresh(recurringMembershipId)) {
        setError("root", {
          message: _t("You already have an active recurring membership for this, so it can't be purchased again."),
        });
        setIsAutoSubmitting(false);
        return;
      }
    }

    const guestClient = !clientLoginEnabled
      ? { name: data.name, email: data.email, phone: data.phone }
      : undefined;

    try {
      await cartCheckout(guestClient ? { client: guestClient } : undefined);
    } catch (err) {
      setError("root", { message: err?.message || "Checkout failed" });
      setIsAutoSubmitting(false);
    }
  };

  const handleAuthSuccess = useAuthSuccessHandler({
    form,
    onSubmit: handleProceed,
    setIsAutoSubmitting,
  });

  if (termsLoading) {
    return (
      <Container>
        <NavigationHeader title="Order Summary" back={-1} />
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader title="Order Summary" back={-1} />

      <Summary>
        <SummaryHeader>
          <Title size="h3">Your Order</Title>
          <Text>Review your selection before continuing to payment</Text>
        </SummaryHeader>

        {lines.length === 0 && (
          <StyledEmptyView icon={Icon.Cart} title="your_cart_is_empty" description="Add items to continue with your order" />
        )}

        {lines.length > 0 && (
          <>
            <SummaryRows>
              {lines.map((line) => (
                <CartLineRow
                  key={line.clientId}
                  line={line}
                  showTypeBadge
                  onQtyChange={setLineQty}
                  onRemove={handleRemoveLine}
                />
              ))}
            </SummaryRows>
            <StyledPriceBreakdown
              currency={cartSummary.currency}
              subtotal={cartSummary.subtotal}
              taxes={cartSummary.taxes}
              price={cartSummary.total}
            />
          </>
        )}
      </Summary>

      <div ref={heldHeight.ref} style={heldHeight.style}>
        {lines.length > 0 && isAutoSubmitting && <LoadingIndicator page />}
        {lines.length > 0 && !isAutoSubmitting && (
          <>
            {showAuth && <ClientAuth onAuthSuccess={handleAuthSuccess} />}
            {showForm && (
              <Form onSubmit={handleSubmit(handleProceed)} noValidate>
                <FormFieldsWrapper>
                  {showGuestFields && <GuestClientFields control={control} errors={errors} />}
                  <ClientTermsCheckboxes form={form} terms={terms} />
                </FormFieldsWrapper>
                <ButtonGroup gap="medium" itemsWidth="half">
                  <Button
                    type="button"
                    variant="secondary"
                    title="Continue shopping"
                    onClick={handleContinueShopping}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    title="Proceed to checkout"
                    disabled={lines.length === 0 || !isComplete || isSubmitting}
                    loading={isPending || isSubmitting}
                  />
                </ButtonGroup>
                {(errors.root || checkoutError) && (
                  <FormError>{errors.root?.message || checkoutError?.message}</FormError>
                )}
              </Form>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default OrderSummary;
